import { useLanguage } from "../hooks/useLanguage";
import { DEFAULT_TAX_CONFIG, LEGACY_WORK_CONFIG, calcPayrollDeductions } from "../firebase/schema";
import DeductionBreakdown from "./DeductionBreakdown";

// Toàn bộ bảng chi tiết lương của một tháng: các khoản thu → bảo hiểm → thuế → thực nhận.
// Dùng chung cho dashboard promoter, bảng lương admin và bảng lương supervisor,
// để ba vai trò thấy đúng cùng một cách diễn giải chứ không chỉ cùng con số cuối.
//
// row: kết quả của computeMonthlyPay (+ baseSalary, dependents, startDate của promoter), ded tuỳ chọn
export default function SalaryBreakdown({
  row, taxCfg = DEFAULT_TAX_CONFIG, workCfg = LEGACY_WORK_CONFIG, month, notice = null,
}) {
  const { t } = useLanguage();
  if (!row) return null;

  const money = (v) => Number(v || 0).toLocaleString("vi-VN") + " đ";
  const sub = { color: "#777", fontSize: 13 };

  // Nơi gọi đã tính sẵn thì dùng lại; chưa có thì tính tại chỗ với cùng tham số.
  const ded = row.ded || calcPayrollDeductions({
    totalIncome: row.total, baseSalary: row.baseSalary || 0,
    allowance: row.allowance || 0, dependents: row.dependents || 0,
    nonTaxable: row.nonTaxable ?? row.otNonTax ?? 0,
    startDate: row.startDate || null, month, cfg: taxCfg,
  });

  const hitPct = row.sellTarget > 0 ? Math.round((row.sold / row.sellTarget) * 100) : null;
  const units = t("home.units");
  const holidayDays = row.paidHolidayDays || row.holidayCredits || 0;

  return (
    <div className="salary-breakdown">
      {notice}

      <div className="salary-row"><span>{t("salary.base")}</span><span>{money(row.baseSalary)}</span></div>
      <div className="salary-row" style={sub}>
        <span>{t("salary.days_worked")}</span>
        <span>{row.dayCreditTotal ?? "—"} {t("x.workday")}</span>
      </div>
      {holidayDays > 0 && (
        <div className="salary-row" style={sub}>
          <span>{t("salary.holiday_days")}</span><span>{holidayDays} {t("x.workday")}</span>
        </div>
      )}

      <div className="salary-row">
        <span>
          {t("salary.work_pay")}
          {row.holidayPay > 0 && (
            <span style={{ fontSize: 12, color: "#888", fontWeight: 400 }}>
              {" "}({t("pay.incl_holiday", { n: row.paidHolidayDays })})
            </span>
          )}
        </span>
        <span>{money(row.workPay)}</span>
      </div>
      {row.holidayOtPay > 0 && (
        <div className="salary-row" style={sub}>
          <span>{t("pay.holiday_ot")} ({row.holidayOtHours ? `${row.holidayOtHours}h` : row.holidayCredits} × {workCfg.holidayOtRate}%)</span>
          <span>{money(row.holidayOtPay)}</span>
        </div>
      )}
      {row.weeklyOffPay > 0 && (
        <div className="salary-row" style={sub}>
          <span>{t("pay.weekly_off")} ({row.weeklyOffHours}h × {workCfg.weeklyOffOtRate}%)</span>
          <span>{money(row.weeklyOffPay)}</span>
        </div>
      )}
      {row.otPay > 0 && (
        <div className="salary-row" style={sub}>
          <span>{t("pay.ot")} ({row.otHours}h × {workCfg.otRate}%)</span>
          <span>{money(row.otPay)}</span>
        </div>
      )}
      <div className="salary-row" style={sub}><span>{t("salary.allowance")}</span><span>{money(row.allowance)}</span></div>
      <div className="salary-row" style={sub}>
        <span>
          {t("salary.sales_bonus")}
          {row.sellTarget > 0 && ` (${row.sold}/${row.sellTarget} ${units} — ${hitPct}%)`}
        </span>
        <span>{money(row.salesBonus)}</span>
      </div>
      <div className="salary-row" style={sub}>
        <span>
          {t("salary.highend_bonus")}
          {row.highendTarget > 0 && ` (${row.highendSold}/${row.highendTarget} ${units})`}
        </span>
        <span>{money(row.highendBonus)}</span>
      </div>
      <div className="salary-row" style={sub}><span>{t("salary.program_bonus")}</span><span>{money(row.bonusProgram)}</span></div>
      <div className="salary-row" style={sub}><span>{t("salary.event_bonus")}</span><span>{money(row.eventBonus)}</span></div>
      {row.otNonTax > 0 && (
        <div className="salary-row" style={sub}><span>{t("salary.ot_nontax")}</span><span>{money(row.otNonTax)}</span></div>
      )}

      <div className="salary-row" style={{ fontWeight: 600, borderTop: "1px solid #eee", paddingTop: 10, marginTop: 8 }}>
        <span>{t("inc.total_income")}</span><span>{money(row.total)}</span>
      </div>

      <DeductionBreakdown ded={ded} taxCfg={taxCfg} />
    </div>
  );
}
